import {
  faArrowUp,
  faArrowUpArrowDown,
  faTableList,
  faChevronRight,
  faChevronDown,
} from '@fortawesome/pro-regular-svg-icons'
import { Box, Flex, Text, Radio } from '@mantine/core'
import {
  DataTable as MantineDataTable,
  type DataTableProps as MantineDataTableProps,
  type DataTableColumn as MantineDataTableColumn,
} from 'mantine-datatable'
import {
  type ReactNode,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'

import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any } from '@flecto/types'

import { Icon } from '../Icon/Icon'

export type * from 'mantine-datatable'

export type DataTableProps<T> = Omit<
  MantineDataTableProps<T>,
  'records' | 'page' | 'totalRecords' | 'onPageChange'
> & {
  records?: DataTableRecord<T>[]
  withBorder?: boolean
  withPagination?: boolean
  withPaginationControlled?: {
    currentPage: number
    totalRecords: number
    onPageChange: (page: number) => void
  }
  withPageSize?: number
  isLoading?: boolean
  hideColumns?: number[]
  emptyStateTitle?: ReactNode
  emptyStateActions?: ReactNode[]
  onSelectSingleRecordChange?: (row: T) => void
  selectedRecord?: DataTableRecord<T>
  groupCollapsible?: boolean
}

export type DataTableRecord<T> = T & {
  id?: string | number
  group?: string
  isGroupHeader?: boolean
}

export type DataTableColumn<T> = MantineDataTableColumn<T>

export function DataTable<T>({
  columns,
  records,
  isLoading = false,
  withBorder = true,
  withPageSize = 10,
  withPagination = true,
  pinFirstColumn,
  pinLastColumn,
  onSelectSingleRecordChange,
  selectedRecord,
  withPaginationControlled,
  hideColumns,
  emptyStateTitle,
  emptyStateActions,
  rowExpansion,
  groupCollapsible = false,
  ...props
}: DataTableProps<T>) {
  const { t } = useTranslation('shared', { keyPrefix: 'data_table' })
  const [pageSize, _setPageSize] = useState(withPageSize)
  const [currentPage, setCurrentPage] = useState(1)
  const [localRecords, setLocalRecords] = useState<DataTableRecord<T>[]>([])
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [singleRecordSelected, setSingleRecordSelected] = useState<T | null>(
    null
  )

  const toggleGroupCollapse = useCallback((group: string) => {
    setCollapsedGroups((prev) => {
      const newCollapsedGroups = new Set(prev)
      if (newCollapsedGroups.has(group)) {
        newCollapsedGroups.delete(group)
      } else {
        newCollapsedGroups.add(group)
      }
      return newCollapsedGroups
    })
  }, [])

  const localColumns = useMemo(() => {
    let filteredColumns = columns ?? []

    if (groupCollapsible) {
      filteredColumns = [
        {
          accessor: 'group_toggle',
          title: '',
          render: (record: T) => {
            const row = record as DataTableRecord<T>
            if ((row as UNSAFE_any).isGroupHeader && row.group) {
              const isCollapsed = collapsedGroups.has(row.group)
              return (
                <Box
                  onClick={() => toggleGroupCollapse(row.group!)}
                  style={{
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  <Icon
                    icon={isCollapsed ? faChevronRight : faChevronDown}
                    size='xs'
                    style={{
                      padding: '5px 7px',
                      border: '1px solid var(--mantine-color-gray-4)',
                      borderRadius: 'var(--mantine-radius-md)',
                      color: 'var(--mantine-color-gray-7)',
                    }}
                  />
                </Box>
              )
            }
            return null
          },
          width: 40,
        },
        ...filteredColumns,
      ]
    }

    if (onSelectSingleRecordChange) {
      filteredColumns = [
        {
          accessor: 'row_radio_selection',
          title: '',
          render: (row) => {
            return (
              <Radio
                checked={singleRecordSelected === row}
                onClick={() => setSingleRecordSelected(row)}
              />
            )
          },
        },
        ...filteredColumns,
      ]
    }

    if (hideColumns) {
      filteredColumns = filteredColumns.filter(
        (_column, index) => !hideColumns.includes(index + 1)
      )
    }

    return filteredColumns
  }, [
    columns,
    singleRecordSelected,
    groupCollapsible,
    collapsedGroups,
    onSelectSingleRecordChange,
    hideColumns,
    toggleGroupCollapse,
  ])

  const showPagination = useMemo(
    () =>
      withPaginationControlled ||
      (withPagination && (records?.length ?? 0) > pageSize),
    [records, pageSize, withPagination, withPaginationControlled]
  )

  const totalRecords = useMemo(() => {
    if (withPaginationControlled) {
      return withPaginationControlled.totalRecords
    } else {
      return records?.length ?? 0
    }
  }, [records, withPaginationControlled])

  const groupRowsByGroup = useCallback(
    (records: DataTableRecord<T>[]) => {
      try {
        if (!Array.isArray(records) || records.length === 0) {
          return []
        }
        if (records.some((r) => r.isGroupHeader)) {
          const result: DataTableRecord<T>[] = []
          for (const row of records) {
            if (row.isGroupHeader && row.group) {
              result.push(row)
            } else if (row.group) {
              if (!collapsedGroups.has(row.group)) {
                result.push(row)
              }
            } else {
              result.push(row)
            }
          }
          return result
        }
        const groupedRecords = new Map<string, DataTableRecord<T>[]>()
        for (const item of records) {
          if (item?.group) {
            if (!groupedRecords.has(item.group)) {
              groupedRecords.set(item.group, [])
            }
            groupedRecords.get(item.group)?.push(item)
          }
        }
        const result: DataTableRecord<T>[] = []
        for (const [group, items] of groupedRecords) {
          const groupHeaderRow = {
            id: group,
            group,
            isGroupHeader: true,
          } as DataTableRecord<T>
          result.push(groupHeaderRow)
          if (!collapsedGroups.has(group)) {
            result.push(...items)
          }
        }
        return result
      } catch (error) {
        console.error('Error in groupRowsByGroup:', error)
        return Array.isArray(records) ? [...records] : []
      }
    },
    [collapsedGroups]
  )

  const handleSelectSingleRecord = ({ record }: { record: T }) => {
    setSingleRecordSelected(record)
    onSelectSingleRecordChange?.(record)
  }

  const handleChangePage = (page: number) => {
    setCurrentPage(page)
    if (withPaginationControlled) {
      withPaginationControlled.onPageChange(page)
    }
  }

  const updateRecords = useCallback(() => {
    const shouldGroupRecords =
      Array.isArray(records) &&
      (typeof records?.[0]?.group === 'string' ||
        records?.some((r) => r.isGroupHeader))

    if (!showPagination || withPaginationControlled) {
      if (shouldGroupRecords && groupCollapsible) {
        const groupedRecords = groupRowsByGroup(records)
        setLocalRecords(groupedRecords ?? [])
        return
      }
      setLocalRecords(records ?? [])
      return
    }

    const from = (currentPage - 1) * pageSize
    const to = from + pageSize

    if (shouldGroupRecords && groupCollapsible) {
      const groupedRecords = groupRowsByGroup(records)
      setLocalRecords((groupedRecords ?? []).slice(from, to))
      return
    }
    setLocalRecords((records ?? []).slice(from, to))
  }, [
    records,
    currentPage,
    pageSize,
    showPagination,
    withPaginationControlled,
    groupRowsByGroup,
    groupCollapsible,
  ])

  useEffect(() => {
    updateRecords()
  }, [updateRecords])

  useEffect(() => {
    if (withPaginationControlled) {
      setCurrentPage(withPaginationControlled.currentPage)
    }
  }, [withPaginationControlled])

  useEffect(() => {
    if (selectedRecord) {
      setSingleRecordSelected(selectedRecord)
    } else {
      setSingleRecordSelected(null)
    }
  }, [selectedRecord])

  return (
    // @ts-expect-error Missing props due to overload
    <MantineDataTable
      {...props}
      pinFirstColumn={pinFirstColumn}
      pinLastColumn={pinLastColumn}
      withTableBorder={withBorder}
      highlightOnHover={
        Boolean(props.onRowClick) || Boolean(onSelectSingleRecordChange)
      }
      onRowClick={
        onSelectSingleRecordChange
          ? handleSelectSingleRecord
          : (props.onRowClick ?? undefined)
      }
      columns={localColumns}
      records={localRecords}
      page={showPagination ? currentPage : (undefined as unknown as number)}
      totalRecords={totalRecords}
      recordsPerPage={pageSize}
      fetching={isLoading}
      onPageChange={handleChangePage}
      paginationSize='md'
      sortIcons={{
        sorted: <Icon icon={faArrowUp} color='gray.7' size='xs' />,
        unsorted: <Icon icon={faArrowUpArrowDown} size='xs' />,
      }}
      rowClassName={(row) => {
        if ('id' in (row as UNSAFE_any)) {
          const rowId = (row as UNSAFE_any).id

          const ids = (props.selectedRecords ?? []).map((rec) => {
            if ('id' in (rec as UNSAFE_any)) {
              return (rec as UNSAFE_any).id
            }
            return false
          })

          if ('id' in ((singleRecordSelected || {}) as UNSAFE_any)) {
            ids.push((singleRecordSelected as UNSAFE_any).id)
          }

          const selected = ids?.includes(rowId)
            ? 'mantine-datatable-row-selected'
            : undefined

          return selected
        }

        return undefined
      }}
      rowExpansion={rowExpansion}
      emptyState={
        <Flex direction='column' gap='sm' align='center'>
          {props.emptyState ? (
            props.emptyState
          ) : (
            <>
              <Icon
                icon={faTableList}
                size='lg'
                data-testid='empty_state_icon'
                withCircleBackground
              />
              <Text fw={500}>{emptyStateTitle ?? t('empty_state_title')}</Text>
            </>
          )}
          {emptyStateActions && (
            <Flex mt='md' gap='sm' align='center' justify='center'>
              {emptyStateActions.map((action, key) => (
                <Box key={key}>{action}</Box>
              ))}
            </Flex>
          )}
        </Flex>
      }
      noRecordsText=''
    />
  )
}
